/**
 * Tous les textes visibles de l'application. Aucune chaîne en dur dans les composants.
 * Règles : tutoiement, pas de tiret cadratin, espace insécable avant « : ; ! ? »,
 * guillemets « », apostrophe typographique, verbe d'action sur les boutons.
 */
import type { EnvelopeIconName } from '@/ui/icons/registry'

const envelopeIcon: Record<EnvelopeIconName, string> = {
  wallet: 'Portefeuille',
  rent: 'Logement',
  building: 'Immeuble',
  groceries: 'Courses',
  restaurant: 'Restaurant',
  coffee: 'Café',
  car: 'Voiture',
  bus: 'Bus',
  taxi: 'Taxi',
  motorbike: 'Moto',
  fuel: 'Carburant',
  plane: 'Voyage',
  health: 'Santé',
  hospital: 'Hôpital',
  school: 'École',
  books: 'Livres',
  gift: 'Cadeaux',
  party: 'Fêtes',
  games: 'Jeux',
  music: 'Musique',
  cinema: 'Cinéma',
  phone: 'Téléphone',
  internet: 'Internet',
  electricity: 'Électricité',
  water: 'Eau',
  gas: 'Gaz',
  repairs: 'Réparations',
  clothes: 'Vêtements',
  baby: 'Enfants',
  love: 'Famille',
  pets: 'Animaux',
  sport: 'Sport',
  church: 'Église',
  mosque: 'Mosquée',
  savings: 'Épargne',
  goal: 'Objectif',
  bank: 'Banque',
  work: 'Travail',
  cash: 'Espèces',
  plants: 'Jardin',
  celebration: 'Anniversaires',
  tickets: 'Sorties',
}

const NB = String.fromCharCode(0xa0)

export const fr = {
  app: {
    name: 'Centime',
  },
  notFound: {
    title: 'Cette page n’existe pas.',
    action: 'Revenir à l’accueil',
  },
  auth: {
    tagline: 'Chaque franc a une place.',
    email: 'Adresse e-mail',
    emailPlaceholder: 'toi@exemple.com',
    password: 'Mot de passe',
    newPassword: 'Nouveau mot de passe',
    passwordHint: '8 caractères minimum, avec des lettres et des chiffres.',
    signIn: {
      title: 'Content de te revoir',
      subtitle: 'Connecte-toi pour retrouver tes enveloppes.',
      submit: 'Se connecter',
      forgot: 'Mot de passe oublié ?',
      noAccount: 'Pas encore de compte ?',
      toSignUp: 'Créer un compte',
    },
    signUp: {
      title: 'Crée ton compte',
      subtitle:
        'Répartis chaque revenu dans des enveloppes et sache ce que tu peux encore dépenser.',
      submit: 'Créer mon compte',
      hasAccount: 'Déjà un compte ?',
      toSignIn: 'Se connecter',
      legal: 'En créant un compte, tu acceptes les',
      legalLink: 'conditions d’utilisation de Centime',
      existingTitle: 'Tu as déjà un compte avec cette adresse.',
      existingBody:
        'Si tu utilisais l’ancienne version de Centime, ton compte a été conservé. Connecte-toi avec ton mot de passe habituel, ou choisis-en un nouveau.',
      existingReset: 'Choisir un nouveau mot de passe',
    },
    checkEmail: {
      title: 'Vérifie ta boîte e-mail',
      body: (email: string) =>
        `Nous avons envoyé un code à 6 chiffres à ${email}. Tape-le ici pour activer ton compte.`,
      spam: 'Rien reçu ? Regarde dans les courriers indésirables, puis renvoie le code.',
      resend: 'Renvoyer le code',
      resendIn: (seconds: number) => `Renvoyer dans ${String(seconds)}\u00a0s`,
      resent: 'Nouveau code envoyé.',
      back: 'Revenir à la connexion',
    },
    code: {
      label: 'Code à 6 chiffres',
      submit: 'Valider le code',
      invalid: 'Ce code n’est pas bon ou a expiré. Vérifie-le, ou demande un nouveau code.',
    },
    forgot: {
      title: 'Mot de passe oublié',
      subtitle:
        'Indique ton adresse : nous t’envoyons un code pour choisir un nouveau mot de passe.',
      submit: 'Envoyer le code',
      sentTitle: 'Tape le code reçu',
      sentBody: (email: string) =>
        `Si un compte existe pour ${email}, un code à 6 chiffres vient d’y être envoyé. Il est valable 1\u00a0heure.`,
      back: 'Revenir à la connexion',
    },
    reset: {
      title: 'Choisis un nouveau mot de passe',
      subtitle: 'Il remplacera l’ancien sur tous tes appareils.',
      submit: 'Enregistrer le mot de passe',
      done: 'Mot de passe modifié.',
      expiredTitle: 'Ce code n’est plus valable',
      expiredBody:
        'Les codes de réinitialisation expirent après 1 heure et ne servent qu’une fois. Demandes-en un nouveau.',
      requestAgain: 'Demander un nouveau code',
    },
    callback: {
      loading: 'Connexion en cours…',
      failedTitle: 'Le lien n’a pas fonctionné',
      failedBody: 'Il a peut-être expiré ou déjà servi. Connecte-toi ou demande un nouveau lien.',
      confirmed: 'Adresse confirmée. Bienvenue !',
    },
    validation: {
      emailRequired: 'Indique ton adresse e-mail.',
      emailInvalid: 'Cette adresse ne semble pas valide. Vérifie-la.',
      passwordRequired: 'Indique ton mot de passe.',
      'too-short': 'Il faut au moins 8 caractères.',
      'needs-letter': 'Ajoute au moins une lettre.',
      'needs-digit': 'Ajoute au moins un chiffre.',
    },
    errors: {
      invalidCredentials:
        'Adresse ou mot de passe incorrect. Vérifie-les ou réinitialise ton mot de passe.',
      emailNotConfirmed:
        'Ton adresse n’est pas encore confirmée. Tape le code reçu par e-mail, ou renvoie-le.',
      emailTaken: 'Un compte existe déjà avec cette adresse. Connecte-toi.',
      weakPassword:
        'Ce mot de passe est trop simple. Utilise au moins 8 caractères avec des lettres et des chiffres.',
      samePassword: 'Choisis un mot de passe différent de l’actuel.',
      invalidEmail: 'Cette adresse ne semble pas valide. Vérifie-la.',
      tooManyEmails: 'Trop d’e-mails envoyés à cette adresse. Réessaie dans quelques minutes.',
      tooManyRequests: 'Trop de tentatives. Patiente une minute avant de réessayer.',
      linkExpired: 'Ce lien a expiré ou a déjà servi. Demandes-en un nouveau.',
      linkOtherDevice: 'Ouvre le lien sur l’appareil où tu as fait la demande.',
      reauthenticationNeeded: 'Pour ta sécurité, reconnecte-toi avant de changer ton mot de passe.',
      sessionExpired: 'Ta session a expiré. Reconnecte-toi.',
      signupDisabled: 'Les inscriptions sont fermées pour le moment.',
      network: 'Pas de connexion. Vérifie ton réseau, puis réessaie.',
      unknown: 'Une erreur inattendue s’est produite. Réessaie dans un instant.',
    },
  },
  nav: {
    main: 'Principal',
    manage: 'Gestion',
    allocate: 'Répartir',
    admin: 'Administration',
    account: 'Mon compte',
    home: 'Accueil',
    envelopes: 'Enveloppes',
    activity: 'Activité',
    analytics: 'Analyses',
    settings: 'Réglages',
    add: 'Ajouter une dépense',
    goals: 'Objectifs',
  },
  shell: {
    offlineTitle: 'Hors ligne',
    offlineBody: 'Tu peux continuer : ce que tu enregistres sera envoyé au retour du réseau.',
    loading: 'Chargement…',
    comingSoon: 'Bientôt disponible',
  },
  envelopes: {
    title: 'Enveloppes',
    emptyTitle: 'Aucune enveloppe',
    emptyBody: 'Crée ta première enveloppe : loyer, nourriture, transport, épargne…',
    archivedCount: (count: number) =>
      `${String(count)} enveloppe${count > 1 ? 's' : ''} supprimée${count > 1 ? 's' : ''}`,
  },
  activity: {
    title: 'Activité',
    emptyTitle: 'Aucun mouvement',
    emptyBody:
      'Chaque revenu, dépense et transfert apparaîtra ici, avec son heure et son enveloppe.',
    recent: 'Activité récente',
    seeAll: 'Tout voir',
    unknownEnvelope: 'Enveloppe archivée',
    toUnallocated: 'Non affecté',
    allocation: 'Répartition',
    transfer: 'Transfert',
    shortfall: 'Manque comblé',
    envelopesCount: (count: number) => `${String(count)} enveloppe${count > 1 ? 's' : ''}`,
    emptyRecent: 'Tes mouvements du mois apparaîtront ici.',
    filter: 'Filtrer les mouvements',
    filters: { all: 'Tout', expense: 'Dépenses', income: 'Revenus', other: 'Autres' },
    emptyFiltered: 'Aucun mouvement pour ce filtre.',
    loadError: 'Impossible de charger ce mois. Vérifie ta connexion.',
    retry: 'Réessayer',
    search: 'Rechercher un libellé',
    searchPlaceholder: 'Ex. Marché, taxi',
    filtersButton: 'Filtres',
    filtersCount: (count: number) => `Filtres (${String(count)})`,
    envelope: 'Enveloppe',
    allEnvelopes: 'Toutes les enveloppes',
    min: 'Montant minimum',
    max: 'Montant maximum',
    reset: 'Effacer les filtres',
    removeFilter: (label: string) => `Retirer le filtre ${label}`,
    atLeast: (amount: string) => `Dès ${amount}`,
    atMost: (amount: string) => `Jusqu’à ${amount}`,
    show: (count: number) =>
      count === 0 ? 'Aucun mouvement' : `Voir ${String(count)} mouvement${count > 1 ? 's' : ''}`,
  },
  analytics: {
    title: 'Analyses',
    emptyTitle: 'Pas encore de données',
    emptyBody: 'Après quelques jours de dépenses, tu verras ici ton rythme et tes alertes.',
    received: 'Reçu',
    spent: 'Dépensé',
    net: 'Reste du mois',
    byEnvelope: 'Dépenses par enveloppe',
    byDay: 'Dépenses par jour',
    dayChart: 'Dépenses par jour du mois',
    alerts: 'À surveiller',
    noAlert: 'Aucune enveloppe ne demande ton attention.',
    share: (percent: number) => `${String(percent)}\u00a0%`,
    perDay: (amount: string) => `${amount} par jour jusqu’à la fin du mois`,
    reasons: {
      overdrawn: 'Solde négatif',
      empty: 'Enveloppe vide',
      'pace-exceeds-funds': 'Au rythme actuel, l’enveloppe sera vide avant la fin du mois',
      'high-spend-ratio': 'Dépenses en avance sur le mois',
    },
  },
  addSheet: {
    title: 'Ajouter',
    kind: 'Type d’opération',
    expense: 'Dépense',
    income: 'Revenu',
    transfer: 'Transfert',
  },
  expense: {
    title: 'Nouvelle dépense',
    amount: 'Montant',
    envelope: 'Enveloppe débitée',
    envelopeError: 'Choisis l’enveloppe à débiter.',
    label: 'Libellé',
    labelPlaceholder: 'Facultatif, ex. Marché, taxi',
    date: 'Date',
    save: 'Enregistrer la dépense',
    saved: 'Dépense enregistrée.',
    noEnvelope: 'Crée d’abord une enveloppe pour enregistrer une dépense.',
    recent: 'Dernière',
    suggestions: 'Libellés déjà utilisés',
  },
  shortfall: {
    body: 'Choisis d’où prendre la différence : l’opération sera enregistrée juste après.',
    fromUnallocated: 'Prendre dans le non affecté',
    from: (name: string) => `Prendre dans ${name}`,
    none: 'Aucune enveloppe ne peut couvrir ce manque. Enregistre un revenu ou réduis le montant.',
    edit: 'Modifier le montant',
    reason: 'Manque comblé',
  },
  transfer: {
    title: 'Transfert entre enveloppes',
    from: 'Depuis',
    to: 'Vers',
    amount: 'Montant',
    note: 'Note',
    notePlaceholder: 'Facultatif',
    choose: 'Choisir une enveloppe',
    save: 'Transférer',
    saved: 'Transfert enregistré.',
    missing: 'Choisis les deux enveloppes.',
    tooFew: 'Il faut au moins deux enveloppes pour faire un transfert.',
    refusals: {
      'same-envelope': 'Choisis deux enveloppes différentes.',
      'source-archived': 'L’enveloppe de départ est archivée.',
      'destination-archived': 'L’enveloppe d’arrivée est archivée.',
      'source-outgoing-forbidden': 'L’enveloppe de départ n’autorise aucun transfert sortant.',
      'destination-incoming-forbidden': 'L’enveloppe d’arrivée n’accepte aucun transfert entrant.',
      'destination-not-allowed-by-source':
        'L’enveloppe de départ n’autorise pas les transferts vers celle-ci.',
      'source-not-allowed-by-destination':
        'L’enveloppe d’arrivée n’accepte pas les transferts depuis celle-ci.',
    },
    available: (amount: string) => `Disponible\u00a0: ${amount}`,
  },
  bills: {
    title: 'À payer ce mois',
    due: (date: string) => `Échéance le ${date}`,
    inDays: (days: number) => (days === 1 ? 'Demain' : `Dans ${String(days)} jours`),
    pay: 'Marquer comme payé',
    paid: 'Paiement enregistré.',
    amount: 'Montant payé',
    envelope: 'Payé depuis',
    allPaid: 'Toutes tes échéances du mois sont réglées.',
    reserved: 'mis de côté',
    paidOn: (date: string) => `Échéance du ${date} · réglée`,
    nextMonth: 'Attendu à partir du mois prochain',
  },
  movement: {
    kind: {
      income: 'Revenu',
      expense: 'Dépense',
      transfer: 'Transfert',
      allocation: 'Répartition',
      adjustment: 'Ajustement',
    },
    edit: 'Modifier',
    editTitle: {
      income: 'Modifier le revenu',
      expense: 'Modifier la dépense',
      transfer: 'Modifier le transfert',
      allocation: 'Modifier la répartition',
      adjustment: 'Modifier le montant',
    },
    saveEdit: 'Enregistrer les modifications',
    edited: 'Opération modifiée.',
    reverse: 'Supprimer',
    reverseTitle: 'Supprimer cette opération ?',
    reverseBody:
      'Les soldes reviennent à leur état d’avant. Une trace reste visible dans les opérations supprimées du journal.',
    reverseConfirm: 'Supprimer',
    keep: 'Garder',
    reverseReason: 'Supprimée par l’utilisateur',
    reversed: 'Opération supprimée.',
    reversedNote: 'Cette opération a été supprimée ou modifiée.',
    pastPeriod: 'Seules les opérations du mois en cours peuvent être modifiées ou supprimées.',
    notEditable:
      'Une répartition ne se modifie pas : supprime-la, puis refais-la depuis l’écran Répartir.',
    showDeleted: 'Afficher les opérations supprimées',
  },
  recurring: {
    title: 'Échéances et revenus réguliers',
    subtitle: 'Paiements fixes et revenus qui reviennent chaque mois.',
    incomeTitle: 'Revenus réguliers',
    expenseTitle: 'Paiements fixes',
    addIncome: 'Ajouter un revenu régulier',
    addExpense: 'Ajouter un paiement fixe',
    emptyIncome: 'Aucun revenu régulier.',
    emptyExpense: 'Aucun paiement fixe.',
    newIncome: 'Nouveau revenu régulier',
    newExpense: 'Nouveau paiement fixe',
    edit: 'Modifier l’échéance',
    name: 'Libellé',
    amount: 'Montant prévu',
    envelope: 'Enveloppe débitée',
    envelopeError: 'Choisis l’enveloppe à débiter.',
    day: 'Chaque mois, le',
    every: (day: number) => (day === 1 ? 'Le 1er du mois' : `Le ${String(day)} du mois`),
    save: 'Enregistrer',
    saved: 'Échéance enregistrée.',
    stop: 'Supprimer cette échéance',
    stopTitle: 'Supprimer cette échéance ?',
    stopBody:
      'Elle n’apparaîtra plus dans tes prochains mois. Les paiements déjà enregistrés restent dans l’historique.',
    stopped: 'Échéance supprimée.',
    paidThisMonth: (day: string) => `Celle du ${day} est-elle déjà payée\u00a0?`,
    paidYes: 'Oui, déjà payée',
    paidNo: 'Pas encore',
    paidYesHint: 'Elle sera attendue à partir du mois prochain.',
    paidNoHint:
      'Elle apparaîtra « En retard » dans « À payer ce mois » : tu la coches dès que c’est fait.',
  },
  settings: {
    title: 'Réglages',
    account: 'Compte',
    email: 'Adresse e-mail',
    signOut: 'Se déconnecter',
    signOutConfirmTitle: 'Te déconnecter ?',
    signOutConfirmBody:
      'Tes données restent enregistrées. Il faudra ton mot de passe pour revenir.',
    signOutConfirm: 'Se déconnecter',
    changePassword: 'Changer mon mot de passe',
    changeEmail: 'Changer mon adresse e-mail',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirme le nouveau mot de passe',
    passwordMismatch: 'Les deux mots de passe ne correspondent pas.',
    passwordChanged: 'Mot de passe modifié.',
    newEmail: 'Nouvelle adresse e-mail',
    sameEmail: 'C’est déjà ton adresse actuelle.',
    emailChangeSent: (email: string) =>
      `Deux liens de confirmation sont partis\u00a0: l’un vers ton adresse actuelle, l’autre vers ${email}. Ouvre les deux pour valider le changement.`,
    confirm: 'Valider',
    terms: 'Conditions d’utilisation',
    app: 'Application',
    install: 'Installer l’application',
    installHint: 'Sur ton écran d’accueil, ouverture instantanée',
    installed: 'Application installée sur cet appareil',
    danger: 'Données',
    deleteAccount: 'Supprimer mon compte',
    deleteHint: 'Efface ton compte et toutes tes données',
    deleteTitle: 'Supprimer ton compte ?',
    deleteBody:
      'Tes enveloppes, opérations, échéances et réglages seront effacés immédiatement. Cette action est définitive : rien ne pourra être récupéré.',
    deletePassword: 'Confirme avec ton mot de passe',
    deleteConfirm: 'Supprimer définitivement',
    deleted: 'Ton compte et tes données ont été supprimés.',
    budget: 'Budget',
    profile: 'Profil et mois budgétaire',
    profileHint: (name: string, day: string) => `${name} · ${day}`,
    profileTitle: 'Profil',
    name: 'Ton prénom',
    namePlaceholder: 'Facultatif',
    startDay: 'Ton mois budgétaire commence',
    startDayHint: 'Le jour où tu reçois habituellement ton revenu principal.',
    save: 'Enregistrer',
    saved: 'Profil enregistré.',
    periodShift: (date: string) =>
      `Ce jour déplacerait le mois en cours et ses opérations. Tu pourras le choisir à partir du ${date}.`,
    recurring: 'Échéances et revenus réguliers',
    recurringHint: 'Paiements fixes, salaire, loyer…',
    notifications: 'Notifications',
    security: 'Sécurité',
    version: (version: string) => `Version ${version}`,
  },
  money: {
    currency: 'Franc CFA (XOF)',
  },
  operations: {
    errors: {
      'duplicate-operation': 'Cette opération est déjà enregistrée.',
      'invalid-amount': 'Indique un montant entier supérieur à 0.',
      'empty-title': 'Donne un libellé à cette opération.',
      'unknown-envelope': 'Cette enveloppe n’existe plus. Actualise la page.',
      'envelope-archived': 'Cette enveloppe est archivée. Choisis-en une autre.',
      'insufficient-funds': 'Fonds insuffisants dans cette enveloppe.',
      'insufficient-unallocated': 'Tu ne peux pas répartir plus que ton argent non affecté.',
      'transfer-refused': 'Les règles de ces enveloppes n’autorisent pas ce transfert.',
      'nothing-to-allocate': 'Indique au moins un montant à répartir.',
      'duplicate-allocation-envelope': 'Une enveloppe apparaît deux fois dans la répartition.',
      'occurrence-already-settled': 'Cette échéance est déjà réglée.',
      'unknown-operation': 'Cette opération est introuvable dans le mois en cours.',
      'already-reversed': 'Cette opération est déjà annulée.',
      'cannot-reverse-reversal': 'Une annulation ne peut pas être annulée.',
      'reversal-would-overdraw':
        'L’argent concerné a déjà été utilisé : l’annulation rendrait un solde négatif.',
      'missing-reason': 'Indique un motif.',
      'invalid-date': 'La date ne peut pas être dans le futur.',
      'period-closed': 'Ce mois est clôturé. Choisis une date du mois en cours.',
      network: 'Pas de connexion. Vérifie ton réseau, puis réessaie.',
      unknown: 'L’opération n’a pas abouti. Réessaie dans un instant.',
    },
    shortfall: (amount: string, envelope: string) => `Il manque ${amount} dans ${envelope}.`,
    unallocatedLimit: (amount: string) => `Tu peux répartir au plus ${amount}.`,
  },
  onboarding: {
    signOut: 'Se déconnecter',
    next: 'Continuer',
    back: 'Retour',
    step: (current: number, total: number) => `Étape ${String(current)} sur ${String(total)}`,
    money: {
      title: 'Combien as-tu en ce moment ?',
      subtitle: 'Espèces, Mobile Money, banque : additionne tout. C’est ton point de départ.',
      amount: 'Ton argent aujourd’hui',
      none: 'Je n’ai rien pour l’instant',
      name: 'Ton prénom',
      namePlaceholder: 'Facultatif',
    },
    income: {
      title: 'Et ton revenu ?',
      subtitle: 'Centime te rappellera son arrivée, sans jamais le compter avant.',
      mode: 'Ton revenu',
      fixed: 'Un salaire fixe',
      variable: 'Des revenus variables',
      amount: 'Montant du salaire',
      day: 'Il arrive le',
      dayOption: (day: number) => (day === 1 ? 'Le 1er du mois' : `Le ${String(day)} du mois`),
      received: 'Déjà reçu ce mois-ci ?',
      yes: 'Oui',
      notYet: 'Pas encore',
      receivedHint: 'Il est déjà compté dans ton argent d’aujourd’hui.',
      variableHint: 'Pas de souci. À chaque rentrée d’argent, touche « Revenu » sur l’accueil.',
      label: 'Salaire',
    },
    budget: {
      title: 'Voilà ton budget',
      subtitle: (amount: string) =>
        `Centime a réparti tes ${amount}, l’essentiel d’abord. Touche un montant pour l’ajuster.`,
      subtitleMonthly:
        'Voici combien prévoir chaque mois dans chaque enveloppe. Touche un montant pour l’ajuster.',
      amountFor: (name: string) => `Montant pour ${name}`,
      rename: (name: string) => `Renommer ${name}`,
      removeOne: 'Retirer cette enveloppe',
      add: 'Ajouter une enveloppe',
      addTitle: 'Nouvelle enveloppe',
      renameTitle: 'Ton enveloppe',
      ideas: 'Idées',
      ideaList: ['Scolarité', 'Tontine', 'Famille', 'Projets', 'Téléphone', 'Dons'],
      name: 'Nom',
      namePlaceholder: 'Ex. Scolarité, Tontine, Famille',
      icon: 'Icône',
      save: 'Enregistrer',
      shared: (done: string, total: string) => `${done} répartis sur ${total}`,
      left: (amount: string) =>
        `${amount} restent à répartir\u00a0: ils t’attendront dans le non affecté.`,
      over: (amount: string) => `Tu dépasses de ${amount}. Baisse un montant.`,
      none: 'Garde au moins une enveloppe.',
      fixedNote: 'Tes paiements fixes (loyer, factures) s’ajoutent ensuite depuis l’accueil.',
      start: 'C’est parti',
      reason: 'Solde de départ',
    },
    steps: {
      settings: {
        startDayOption: (day: number) =>
          day === 1 ? 'Le 1er du mois' : `Le ${String(day)} du mois`,
      },
      fixed: {
        every: (day: number) =>
          day === 1 ? 'Le 1er de chaque mois' : `Le ${String(day)} de chaque mois`,
      },
    },
    templates: {
      fixed: 'Dépenses fixes',
      food: 'Nourriture et eau',
      transport: 'Transport et entretien',
      health: 'Santé et imprévus',
      leisure: 'Voyages et sorties',
      savings: 'Épargne de sécurité',
      projects: 'Projets',
      other: 'Autres',
    },
  },

  envelope: {
    spend: 'Dépenser',
    history: 'Historique',
    kinds: {
      essential: {
        label: 'Essentielle',
        hint: 'Loyer, factures. Protégée : aucun transfert sortant.',
      },
      variable: { label: 'Variable', hint: 'Nourriture, eau, transport du quotidien.' },
      discretionary: {
        label: 'Plaisir',
        hint: 'Sorties, voyages. Peut dépanner les autres enveloppes.',
      },
      savings: { label: 'Épargne', hint: 'Réserve, projets. Protégée contre les retraits.' },
      custom: { label: 'Personnalisée', hint: 'Règles libres.' },
    },
    rollover: {
      label: 'À la fin du mois, le disponible',
      carry: { label: 'Reste dans l’enveloppe', hint: 'Il s’ajoute au mois suivant.' },
      return: { label: 'Revient dans « Non affecté »', hint: 'Tu le répartis de nouveau.' },
      ask: { label: 'Je choisis à la clôture', hint: 'Centime te pose la question chaque mois.' },
    },
    new: 'Nouvelle enveloppe',
    edit: 'Modifier',
    save: 'Enregistrer',
    create: 'Créer l’enveloppe',
    name: 'Nom',
    icon: 'Icône',
    kind: 'Type',
    target: 'Objectif mensuel',
    targetHint: 'Montant visé chaque mois, utilisé pour proposer la répartition.',
    rules: 'Règles',
    protected: 'Enveloppe protégée',
    protectedHint: 'Un transfert sortant demande une confirmation.',
    allowOutgoing: 'Peut dépanner d’autres enveloppes',
    allowIncoming: 'Peut recevoir des transferts',
    allowOverdraft: 'Autoriser un découvert',
    allowOverdraftHint:
      'Déconseillé : une dépense pourra dépasser le disponible, avec avertissement.',
    nameRequired: 'Donne un nom à l’enveloppe.',
    saved: 'Enveloppe enregistrée.',
    created: 'Enveloppe créée.',
    archive: 'Supprimer l’enveloppe',
    archiveTitle: (name: string) => `Supprimer « ${name} »${NB}?`,
    archiveBody: 'Elle disparaît de tes listes. Son historique reste consultable dans Activité.',
    archiveMoney: (amount: string) =>
      `Ses ${amount} reviennent à ton argent non affecté, prêts à être répartis ailleurs.`,
    archiveRules: (count: number) =>
      count === 1
        ? 'Son paiement fixe est supprimé aussi.'
        : `Ses ${String(count)} paiements fixes sont supprimés aussi.`,
    archiveConfirm: 'Supprimer',
    archived: 'Enveloppe supprimée.',
    archiveReserved:
      'De l’argent y est mis de côté pour une échéance. Marque-la comme payée ou supprime-la, puis réessaie.',
    archiveOverdrawn: `Elle est à découvert. Transfère de l’argent pour la remettre à zéro, puis réessaie.`,
    reorder: 'Réorganiser',
    reorderDone: 'Terminé',
    moveUp: (name: string) => `Monter ${name}`,
    moveDown: (name: string) => `Descendre ${name}`,
    available: 'Disponible',
    funded: 'Financé ce mois',
    spent: 'Dépensé',
    reserved: 'Réservé',
    fixedPayments: 'Paiements fixes',
    noFixedPayments: 'Aucun paiement fixe dans cette enveloppe.',
    notFound: 'Cette enveloppe n’existe pas ou a été archivée.',
  },
  income: {
    receive: 'Recevoir un revenu',
    sheetTitle: 'Revenu reçu',
    expected: 'Revenus attendus',
    expectedHint: 'Pas encore dépensables : confirme-les à leur réception.',
    confirm: 'Confirmer la réception',
    oneOff: 'Autre rentrée d’argent',
    title: 'Libellé',
    titlePlaceholder: 'Ex. Mission, vente, cadeau',
    amount: 'Montant reçu',
    date: 'Date',
    save: 'Enregistrer le revenu',
    saved: 'Revenu enregistré.',
    allocateNow: 'Répartir maintenant',
    later: 'Plus tard',
    due: (date: string) => `Prévu le ${date}`,
  },
  goals: {
    title: 'Objectifs d’épargne',
    subtitle: 'L’argent reste dans son enveloppe d’épargne, à l’abri des dépenses du quotidien.',
    emptyTitle: 'Aucun objectif pour l’instant',
    emptyBody: 'Moto, mariage, rentrée scolaire : donne un nom et un montant à ce que tu prépares.',
    new: 'Nouvel objectif',
    create: 'Créer l’objectif',
    edit: 'Modifier l’objectif',
    save: 'Enregistrer',
    name: 'Nom de l’objectif',
    namePlaceholder: 'Ex. Moto, mariage, rentrée',
    nameRequired: 'Donne un nom à ton objectif.',
    target: 'Montant visé',
    hasDate: 'Fixer une date',
    hasDateHint: 'Centime calcule ce qu’il faut mettre de côté chaque mois.',
    date: 'Date visée',
    dateInPast: 'Choisis une date à venir.',
    envelope: 'Enveloppe d’épargne',
    newEnvelope: 'Nouvelle enveloppe dédiée',
    newEnvelopeHint: 'Créée et protégée automatiquement',
    of: (amount: string) => `sur ${amount}`,
    remaining: (amount: string) => `Encore ${amount}`,
    perMonth: (amount: string, date: string) => `${amount} par mois pour y arriver le ${date}`,
    reached: 'Objectif atteint. Bravo !',
    contribute: 'Ajouter de l’argent',
    contributeTitle: (name: string) => `Ajouter à ${name}`,
    from: 'Prendre depuis',
    unallocated: 'Argent non affecté',
    noSource:
      'Aucun argent disponible pour l’instant. Enregistre un revenu ou répartis-le d’abord.',
    amount: 'Montant',
    contributed: 'Argent ajouté à l’objectif.',
    created: 'Objectif créé.',
    updated: 'Objectif enregistré.',
    archive: 'Retirer cet objectif',
    archiveTitle: 'Retirer cet objectif ?',
    archiveBody: 'L’argent reste dans son enveloppe. Seul le suivi de l’objectif disparaît.',
    archived: 'Objectif retiré.',
    progress: (name: string) => `Progression de ${name}`,
    seeAll: 'Suis la progression de ce que tu prépares',
  },
  lock: {
    title: 'Centime est verrouillé',
    greeting: (name: string | null) => (name ? `Bonjour ${name}` : 'Bonjour'),
    prompt: 'Saisis ton code pour continuer.',
    wrong: (remaining: number) =>
      remaining > 3
        ? 'Code incorrect.'
        : `Code incorrect. Encore ${String(remaining)} essai${remaining > 1 ? 's' : ''} avant de devoir te reconnecter.`,
    blocked: (seconds: number) =>
      seconds >= 60
        ? `Trop d’essais. Réessaie dans ${String(Math.ceil(seconds / 60))}\u00a0min.`
        : `Trop d’essais. Réessaie dans ${String(seconds)}\u00a0s.`,
    exhausted: 'Trop d’essais. Reconnecte-toi avec ton mot de passe, puis crée un nouveau code.',
    biometric: 'Utiliser Face ID ou l’empreinte',
    forgot: 'Code oublié ?',
    forgotTitle: 'Code oublié ?',
    forgotBody:
      'Tu vas être déconnecté. Reconnecte-toi avec ton mot de passe, puis crée un nouveau code dans les réglages.',
    forgotConfirm: 'Me déconnecter',
  },
  security: {
    title: 'Code de verrouillage',
    settingsHint: (on: boolean) => (on ? 'Activé' : 'Protège l’accès à l’app sur ce téléphone'),
    intro:
      'Un code à 4 chiffres protège l’accès à Centime sur ce téléphone, même si quelqu’un l’a entre les mains.',
    create: 'Créer un code',
    change: 'Changer le code',
    disable: 'Désactiver le code',
    delay: 'Verrouiller',
    delayHint: 'Après ce délai sans utilisation, ou quand l’app passe en arrière-plan.',
    delays: {
      0: 'Dès que l’app est quittée',
      1: 'Après 1\u00a0minute',
      5: 'Après 5\u00a0minutes',
      15: 'Après 15\u00a0minutes',
    } as Record<number, string>,
    biometric: 'Face ID ou empreinte',
    biometricHint: 'Déverrouille sans saisir le code. Le code reste possible.',
    biometricFailed: 'L’empreinte n’a pas pu être enregistrée sur cet appareil.',
    steps: {
      current: 'Saisis ton code actuel',
      new: 'Choisis un code à 4 chiffres',
      confirm: 'Confirme ton code',
    },
    mismatch: 'Les deux codes ne correspondent pas. Recommence.',
    created: 'Code créé. Centime se verrouillera sur ce téléphone.',
    changed: 'Code modifié.',
    disabled: 'Code désactivé.',
    local: 'Le code reste sur ce téléphone : il n’est jamais envoyé, et chaque appareil a le sien.',
  },
  notifications: {
    title: 'Notifications',
    intro: 'Centime te prévient au bon moment, et jamais deux fois pour la même chose.',
    device: 'Sur ce téléphone',
    push: 'Notifications push',
    pushHint: 'Instantanées, même quand l’app est fermée.',
    states: {
      unavailable: 'Les notifications push ne sont pas disponibles sur cette version.',
      unsupported:
        'Ce navigateur ne permet pas les notifications push. Les e-mails restent disponibles.',
      'install-first':
        'Sur iPhone, installe d’abord Centime sur ton écran d’accueil : les notifications s’activent ensuite depuis l’app.',
      denied:
        'Les notifications sont bloquées pour Centime dans les réglages du téléphone. Autorise-les, puis reviens ici.',
    } as Record<string, string>,
    installHow: 'Voir comment installer',
    test: 'Envoyer une notification d’essai',
    testSent: 'Notification d’essai envoyée.',
    testFailed: 'Aucun appareil ne l’a reçue. Réactive les notifications sur ce téléphone.',
    emailSection: 'Par e-mail',
    email: 'Récapitulatif quotidien',
    emailHint: (address: string) => `Un seul e-mail par jour au plus, à ${address}.`,
    news: 'Nouveautés et conseils',
    newsHint: 'Quelques e-mails par mois au plus : astuces, nouvelles fonctions, avantages.',
    timing: 'Moment',
    hour: 'Heure des rappels',
    hourHint: 'Les rappels du jour arrivent à cette heure, dans ton fuseau.',
    hourOption: (hour: number) => `${String(hour)}\u00a0h`,
    kinds: 'Ce que tu reçois',
    bills: 'Échéances',
    billsHint: 'Trois jours avant, le jour même, puis en cas de retard.',
    income: 'Revenus attendus',
    incomeHint: 'À la date prévue, puis l’argent reçu pas encore réparti.',
    envelopes: 'Enveloppes',
    envelopesHint: 'Découvert, enveloppe vide ou dépenses trop rapides.',
    review: 'Bilan du mois',
    reviewHint: 'Quand un mois est terminé.',
    weekly: 'Récapitulatif du dimanche',
    weeklyHint: 'Tes dépenses de la semaine et ce qu’il te reste.',
    nudges: 'Rappel de saisie',
    nudgesHint: 'Après trois jours sans dépense notée.',
    saveFailed: 'Préférence non enregistrée. Vérifie ta connexion.',
    settingsHint: 'Échéances, revenus, bilan, en push et par e-mail',
    prompt: {
      title: 'Ne rate plus une échéance',
      body: 'Active les rappels : Centime te prévient avant chaque paiement et quand ton argent doit arriver.',
      enable: 'Activer les rappels',
      install: 'Installer l’app pour les rappels',
      later: 'Plus tard',
      enabled: 'Rappels activés sur ce téléphone.',
    },
    unsubscribe: {
      title: 'E-mails de rappel',
      working: 'Un instant…',
      done: 'C’est fait : tu ne recevras plus d’e-mails de rappel. Tu peux les réactiver à tout moment dans les réglages.',
      newsTitle: 'Nouveautés et conseils',
      newsDone:
        'C’est fait : tu ne recevras plus nos nouveautés ni nos conseils. Tes rappels ne changent pas.',
      invalid: 'Ce lien n’est plus valide. Gère tes e-mails depuis les réglages de Centime.',
      back: 'Aller sur Centime',
    },
  },
  review: {
    bannerTitle: (month: string) => `${month} est terminé`,
    bannerBody: 'Vois ce qu’il te reste et décide de ce que tu reportes.',
    open: 'Voir le bilan',
    title: (month: string) => `Bilan de ${month}`,
    intro:
      'Rien ne disparaît : l’argent non dépensé reste dans son enveloppe ou revient au non affecté, selon ta décision.',
    received: 'Reçu',
    spent: 'Dépensé',
    left: 'Il te reste',
    envelopes: 'Ce qu’il reste dans tes enveloppes',
    nothingLeft: 'Toutes tes enveloppes ont été dépensées.',
    carry: 'Garder dans l’enveloppe',
    return: 'Rendre au non affecté',
    choice: (name: string) => `Que faire du reste de ${name}\u00a0?`,
    carried: 'Reporté le mois prochain',
    returned: 'Revient au non affecté',
    overdrawn: 'Découvert reporté',
    unallocated: 'Argent non affecté',
    unallocatedHint: 'Il reste disponible pour ta prochaine répartition.',
    confirm: 'Valider le bilan',
    done: (month: string) => `Bilan de ${month} validé.`,
    errors: {
      'closing-would-overdraw':
        'Ce report rendrait une enveloppe négative ce mois-ci. Garde l’argent dans cette enveloppe.',
      'decision-required': 'Choisis quoi faire du reste de chaque enveloppe.',
      'period-not-open': 'Ce mois est déjà clôturé.',
      'previous-period-open': 'Un mois plus ancien attend son bilan.',
    } as Record<string, string>,
  },
  allocation: {
    title: 'Répartir',
    toAllocate: 'À répartir',
    allocated: 'Réparti',
    remaining: 'Reste non affecté',
    over: 'Tu dépasses ton argent disponible.',
    target: (amount: string) => `Objectif ${amount}`,
    already: (amount: string) => `Déjà ${amount} ce mois`,
    suggest: 'Proposer',
    clear: 'Remettre à zéro',
    submit: 'Valider la répartition',
    done: 'Répartition enregistrée.',
    targetsMet:
      'Tes objectifs du mois sont atteints : choisis librement où placer le reste, ou garde-le non affecté.',
    nothing: 'Rien à répartir pour l’instant.',
    nothingHint: 'Enregistre un revenu reçu : il apparaîtra ici.',
    lineLabel: (name: string) => `Montant pour ${name}`,
  },
  help: {
    title: 'Comment ça marche',
    open: 'Comprendre la méthode',
    more: 'Tout comprendre en 2 minutes',
    learnMore: (topic: string) => `En savoir plus sur ${topic}`,
    intro:
      'Centime applique la méthode des enveloppes : chaque franc reçoit un rôle avant d’être dépensé. Tu sais à tout moment ce que tu peux dépenser, sans calcul.',
    stepsTitle: 'La méthode en 5 étapes',
    steps: [
      {
        title: 'Ton argent arrive dans le non affecté',
        body: 'Chaque revenu (salaire, vente, cadeau) va d’abord dans le non affecté. Il n’a pas encore de rôle.',
      },
      {
        title: 'Tu le répartis dans tes enveloppes',
        body: 'Loyer, nourriture, transport, épargne : tu donnes un rôle à chaque franc avec « Répartir ». Le but : un non affecté à zéro.',
      },
      {
        title: 'Tu dépenses depuis une enveloppe',
        body: 'Une dépense se note en deux gestes : le montant, puis l’enveloppe. Son disponible baisse aussitôt.',
      },
      {
        title: 'Une enveloppe vide ? Tu prends ailleurs',
        body: 'Si une dépense dépasse le disponible, Centime te propose de prendre la différence dans une autre enveloppe. Tu choisis, rien n’est caché.',
      },
      {
        title: 'En fin de mois, tu fais le bilan',
        body: 'Ce qui reste est reporté au mois suivant ou rendu au non affecté, selon la règle de chaque enveloppe. Ton épargne grandit d’elle-même.',
      },
    ],
    quickTitle: 'Les gestes rapides',
    quick: [
      'Accueil, « Dépense » : tape le montant, touche l’enveloppe. C’est noté.',
      'Accueil, « Revenu » : tape le montant, touche sa source.',
      'Une erreur ? Touche le mouvement dans l’activité pour le modifier ou le supprimer.',
      'Les paiements fixes (loyer, abonnements) sont réservés à l’avance : tu ne les dépenses pas par mégarde.',
    ],
    glossaryTitle: 'Les mots de Centime',
    topics: {
      available: {
        question: 'Disponible',
        answer:
          'Ce qui reste à dépenser dans tes enveloppes, une fois les paiements fixes réservés. C’est le chiffre à regarder avant d’acheter.',
      },
      unallocated: {
        question: 'Non affecté',
        answer:
          'L’argent reçu qui n’a pas encore de rôle. Il ne se dépense pas directement : répartis-le dans tes enveloppes.',
      },
      envelope: {
        question: 'Enveloppe',
        answer:
          'Un poste de dépense ou d’épargne (nourriture, loyer, projet). Tu y mets de l’argent, tu dépenses depuis elle, tu vois ce qu’il reste.',
      },
      reserved: {
        question: 'Réservé',
        answer:
          'Une part d’enveloppe mise de côté pour une échéance à venir (loyer, facture). Elle est payée avec « Marquer comme payé ».',
      },
      protected: {
        question: 'Enveloppe protégée',
        answer:
          'Une enveloppe (épargne, urgences) dans laquelle Centime ne propose jamais de puiser pour combler un manque.',
      },
      rollover: {
        question: 'Report',
        answer:
          'Ce qui reste dans une enveloppe à la fin du mois. Selon sa règle, il passe au mois suivant, rejoint le non affecté ou t’est demandé au bilan.',
      },
    } as Record<string, { question: string; answer: string }>,
    banner: 'Nouveau sur Centime ? Comprends la méthode en 2 minutes.',
    dismiss: 'Masquer',
  },
  currency: {
    label: 'Devise',
    hint: (example: string) =>
      `Tous tes montants s’affichent dans cette devise, par exemple ${example}. En changer ne convertit pas les montants déjà notés.`,
  },
  firstSteps: {
    title: 'Tes premiers pas',
    progress: (done: number, total: number) => `${String(done)} sur ${String(total)}`,
    expense: 'Note ta première dépense',
    expenseHint: 'Le montant, l’enveloppe, c’est noté.',
    bills: 'Ajoute ton loyer et tes factures',
    billsHint: 'Centime les met de côté et te les rappelle.',
    install: 'Installe Centime sur ton téléphone',
    installHint: 'Une icône, une ouverture instantanée.',
    guide: 'Comprends la méthode en 2 minutes',
    guideHint: 'Cinq étapes simples, en images.',
    done: 'Fait',
    dismiss: 'Masquer les premiers pas',
  },
  quick: {
    label: 'Saisie express',
    expense: 'Dépense',
    income: 'Revenu',
    expenseTitle: 'Nouvelle dépense',
    incomeTitle: 'Nouveau revenu',
    expenseHint: 'Le montant, puis où il est parti.',
    incomeHint: 'Le montant, puis d’où il vient.',
    amount: 'Montant',
    bills: 'Un paiement fixe du mois ?',
    billDue: (day: string, envelope: string) => `Prévu le ${day} · ${envelope}`,
    orEnvelope: 'Sinon, dans quelle enveloppe ?',
    envelopes: 'Dans quelle enveloppe ?',
    noSpendable:
      'Aucune enveloppe pour les dépenses courantes. Crée-en une (nourriture, transport…) depuis Enveloppes.',
    expected: 'Un revenu attendu ?',
    expectedOn: (day: string) => `Prévu le ${day}`,
    orSource: 'Sinon, d’où vient cet argent ?',
    sources: 'D’où vient cet argent ?',
    defaultIncome: 'Revenu',
    needAmount: 'Tape le montant',
    needEnvelope: 'Choisis l’enveloppe',
    needSource: 'Choisis la source',
    save: (amount: string) => `Enregistrer ${amount}`,
    payBill: (title: string, amount: string) => `Payer ${title} · ${amount}`,
    doneTitle: 'C’est noté',
    doneExpense: 'Dépense notée',
    doneBill: (title: string) => `${title} payé`,
    doneIncome: 'Revenu noté',
    remaining: (envelope: string) => `Il reste dans ${envelope}`,
    incomeNext: 'Il attend dans le non affecté. Répartis-le pour donner un rôle à chaque franc.',
    allocateNow: 'Répartir maintenant',
    later: 'Plus tard',
    finish: 'Terminé',
    undo: 'Annuler cette opération',
    undone: 'Opération annulée.',
    more: 'Ajouter un libellé ou une date',
    toAllocate: 'à répartir',
    allocate: 'Répartir',
    allocated: 'Tout ton argent a un rôle.',
  },

  home: {
    greeting: (name: string | null) => (name ? `Bonjour ${name}` : 'Bonjour'),
    available: 'Disponible dans tes enveloppes',
    unallocated: 'Non affecté',
    received: 'Reçu ce mois',
    spent: 'Dépensé ce mois',
    allocate: 'Répartir',
    receive: 'Recevoir',
    expected: 'Revenus attendus',
    envelopes: 'Tes enveloppes',
    seeAll: 'Tout voir',
    emptyTitle: 'Aucune enveloppe',
    emptyBody: 'Crée ta première enveloppe pour commencer à répartir ton argent.',
  },
  mailing: {
    tabs: {
      overview: 'Vue d’ensemble',
      campaigns: 'Campagnes',
      templates: 'Modèles',
      automations: 'Parcours',
      label: 'Sections de l’administration',
    },
    loadError: 'Impossible de charger ces données. Vérifie ta connexion et réessaie.',
    limit:
      'Offre gratuite de Resend : 100 e-mails par jour et 3 000 par mois au plus. Au-delà, les envois échouent jusqu’au lendemain.',
    campaigns: {
      title: 'Campagnes e-mail',
      intro: 'Informe tes utilisateurs des nouveautés, avantages et conseils, quand tu le décides.',
      new: 'Nouvelle campagne',
      empty: 'Aucune campagne pour l’instant. Pars d’un modèle pour aller vite.',
      status: {
        draft: 'Brouillon',
        scheduled: 'Programmée',
        sending: 'En cours d’envoi',
        sent: 'Envoyée',
        cancelled: 'Annulée',
      } as Record<string, string>,
      counts: (sent: number, recipients: number, failed: number) =>
        failed > 0
          ? `${String(sent)} envoyés sur ${String(recipients)}, ${String(failed)} en échec`
          : `${String(sent)} envoyés sur ${String(recipients)}`,
      scheduledFor: (date: string) => `Prévue le ${date}`,
    },
    editor: {
      newTitle: 'Nouvelle campagne',
      editTitle: 'Campagne',
      name: 'Nom interne',
      namePlaceholder: 'Ex. Annonce des rappels, octobre',
      nameRequired: 'Donne un nom à cette campagne.',
      setup: 'Campagne',
      fromTemplate: 'Partir d’un modèle',
      fromTemplateHint: 'Le contenu du modèle est copié : tu peux ensuite tout modifier.',
      choose: 'Choisir un modèle',
      audience: 'Destinataires',
      audienceKinds: {
        all: 'Tous les inscrits',
        onboarded: 'Configuration terminée',
        not_onboarded: 'Configuration non terminée',
        active: 'Actifs récemment',
        inactive: 'Inactifs depuis un moment',
        new: 'Nouveaux inscrits',
        selected: 'Personnes choisies',
      } as Record<string, string>,
      audienceLabel: 'Qui reçoit cet e-mail',
      audienceHints: {
        all: 'Toutes les personnes inscrites et confirmées.',
        onboarded: 'Celles qui ont terminé la configuration de leur budget.',
        not_onboarded: 'Celles qui se sont inscrites sans terminer la configuration.',
        active: 'Celles qui ont noté au moins une opération sur la période.',
        inactive: 'Celles qui n’ont rien noté sur la période.',
        new: 'Celles qui se sont inscrites sur la période.',
        selected: 'Des personnes précises, choisies une à une.',
      } as Record<string, string>,
      chosen: 'Personnes choisies',
      days: 'Période',
      daysOption: (days: number) => `${String(days)} derniers jours`,
      search: 'Rechercher une personne',
      searchPlaceholder: 'Adresse ou prénom',
      add: (email: string) => `Ajouter ${email}`,
      remove: (email: string) => `Retirer ${email}`,
      audienceCount: (count: number) =>
        count === 0
          ? 'Personne n’est visé.'
          : `${String(count)} personne${count > 1 ? 's' : ''} recevra cet e-mail.`,
      audienceNote: 'Les personnes désinscrites des nouveautés ne sont jamais visées.',
      send: 'Envoi',
      saveDraft: 'Enregistrer le brouillon',
      saved: 'Campagne enregistrée.',
      test: 'M’envoyer un test',
      testSent: 'Test envoyé à ton adresse.',
      testFailed: 'Le test n’est pas parti. Vérifie la clé Resend de la fonction d’envoi.',
      scheduleDate: 'Date d’envoi',
      scheduleHour: 'Heure',
      hourOption: (hour: number) => `${String(hour)}\u00a0h`,
      schedule: 'Programmer',
      reschedule: 'Changer la date',
      scheduledNote: 'Tu peux encore la modifier, changer la date ou annuler la programmation.',
      past: 'Choisis une date et une heure à venir.',
      nobody: 'Personne n’est visé par cette campagne. Change les destinataires.',
      failed: 'L’action n’a pas abouti. Vérifie ta connexion et réessaie.',
      duplicated: 'Copie créée. Tu peux la modifier.',
      scheduled: 'Campagne programmée.',
      sendNow: 'Envoyer maintenant',
      sendTitle: (count: number) =>
        `Envoyer à ${String(count)} personne${count > 1 ? 's' : ''}\u00a0?`,
      sendBody: 'L’envoi commence tout de suite et ne peut pas être annulé.',
      sendConfirm: 'Envoyer',
      sending: 'Envoi lancé. Les compteurs se mettent à jour en quelques minutes.',
      cancel: 'Annuler la programmation',
      cancelled: 'Programmation annulée : la campagne repasse en brouillon.',
      delete: 'Supprimer la campagne',
      deleteTitle: 'Supprimer cette campagne ?',
      deleteBody: 'Elle disparaît de la liste. Les e-mails déjà envoyés ne sont pas concernés.',
      deleted: 'Campagne supprimée.',
      duplicate: 'Dupliquer',
      copyOf: (name: string) => `Copie de ${name}`,
      readOnly:
        'Cette campagne est partie : elle ne se modifie plus. Duplique-la pour en préparer une nouvelle.',
      incomplete: 'Ajoute un objet et au moins un bloc de contenu.',
    },
    content: {
      subject: 'Objet',
      subjectHint: 'Écris {{prenom}} pour insérer le prénom du destinataire.',
      preheader: 'Texte d’aperçu',
      preheaderHint: 'Affiché après l’objet dans la boîte de réception.',
      blocks: 'Contenu',
      addBlock: 'Ajouter un bloc',
      types: {
        heading: 'Titre',
        paragraph: 'Paragraphe',
        list: 'Liste',
        callout: 'Encadré',
        button: 'Bouton',
        divider: 'Séparateur',
      } as Record<string, string>,
      listHint: 'Un élément par ligne.',
      buttonLabel: 'Texte du bouton',
      buttonUrl: 'Lien',
      buttonUrlHint:
        'Un écran de Centime (/accueil, /repartir, /objectifs…) ou une adresse complète.',
      moveUp: 'Monter le bloc',
      moveDown: 'Descendre le bloc',
      removeBlock: 'Supprimer le bloc',
      view: 'Affichage',
      edit: 'Contenu',
      preview: 'Aperçu',
      previewTitle: 'Aperçu de l’e-mail',
      emptyBlocks: 'Aucun bloc. Ajoute un titre, un paragraphe ou un bouton.',
    },
    templates: {
      title: 'Modèles d’e-mails',
      editTitle: 'Modèle',
      identity: 'Classement',
      save: 'Enregistrer le modèle',
      intro:
        'Plus de 100 modèles prêts à l’emploi, tous modifiables. Crée les tiens à tout moment.',
      new: 'Nouveau modèle',
      search: 'Rechercher un modèle',
      all: 'Toutes',
      count: (count: number) => `${String(count)} modèle${count > 1 ? 's' : ''}`,
      category: 'Catégorie',
      categoryPlaceholder: 'Ex. Nouveautés',
      name: 'Nom du modèle',
      required: 'Catégorie, nom et objet sont obligatoires.',
      saved: 'Modèle enregistré.',
      delete: 'Supprimer le modèle',
      deleteTitle: 'Supprimer ce modèle ?',
      deleteBody: 'Les campagnes déjà créées avec lui ne sont pas modifiées.',
      deleted: 'Modèle supprimé.',
      use: 'Créer une campagne avec ce modèle',
      empty: 'Aucun modèle ne correspond.',
    },
    automations: {
      title: 'Parcours de découverte',
      intro:
        'Une série d’e-mails envoyés automatiquement après l’inscription, à 10\u00a0h heure locale, pour faire comprendre la méthode pas à pas.',
      day: (day: number) =>
        day === 0
          ? 'Le jour de l’inscription'
          : `${String(day)} jour${day > 1 ? 's' : ''} après l’inscription`,
      template: 'Modèle envoyé',
      sent: (count: number) => `${String(count)} envoyé${count > 1 ? 's' : ''}`,
      dayLabel: 'Jour d’envoi',
      enabled: 'Activée',
      open: (name: string) => `Modifier le modèle «\u00a0${name}\u00a0»`,
      saved: 'Parcours mis à jour.',
      off: 'Toutes les étapes sont désactivées : rien n’est envoyé automatiquement.',
    },
  },
  admin: {
    title: 'Administration',
    subtitle:
      'Inscriptions et usage de Centime. Aucune donnée financière des utilisateurs n’est visible ici.',
    users: 'Inscrits',
    confirmed: 'Adresses confirmées',
    onboarded: 'Configuration terminée',
    newLast7Days: 'Nouveaux (7 jours)',
    activeToday: 'Actifs aujourd’hui',
    activeLast7Days: 'Actifs (7 jours)',
    activeLast30Days: 'Actifs (30 jours)',
    operationsToday: 'Opérations aujourd’hui',
    usage: 'Usage quotidien',
    metric: 'Indicateur',
    metrics: { activeUsers: 'Actifs', signups: 'Inscriptions', operations: 'Opérations' },
    chartLabel: (metric: string) => `${metric} par jour, 14 derniers jours`,
    list: 'Inscrits récents',
    columns: {
      user: 'Utilisateur',
      createdAt: 'Inscription',
      lastActivity: 'Dernière activité',
      activeDays: 'Jours actifs',
      operations: 'Opérations',
      status: 'État',
    },
    never: 'Jamais',
    pending: 'Non confirmé',
    setup: 'En configuration',
    ready: 'Actif',
    loadMore: 'Afficher plus',
    shown: (shown: number, total: number) => `${String(shown)} sur ${String(total)}`,
    operations: (count: number) => `${String(count)} opération${count > 1 ? 's' : ''}`,
    activeDays: (count: number) =>
      `${String(count)} jour${count > 1 ? 's' : ''} actif${count > 1 ? 's' : ''}`,
    error: 'Impossible de charger les statistiques. Réessaie dans un instant.',
  },
  landing: {
    nav: {
      label: 'Sections de la page',
      how: 'Fonctionnement',
      features: 'Fonctions',
      install: 'Application',
      pricing: 'Tarif',
      faq: 'Questions',
      signIn: 'Se connecter',
      start: 'Commencer',
    },
    badge: 'Gratuit, en francs CFA, sans banque',
    heroTitle: 'Le budget par enveloppes, enfin simple.',
    heroBody:
      'Centime répartit chaque revenu entre loyer, nourriture, transport et épargne. Tu sais à tout moment ce que tu peux encore dépenser, sans tableur ni calcul de tête.',
    heroCta: 'Créer mon compte gratuit',
    heroSecondary: 'J’ai déjà un compte',
    preview: {
      label: 'Aperçu de l’application Centime',
      url: 'mescentimes.cloud',
      greeting: 'Bonjour Awa',
      date: 'Jeudi 15 octobre',
      nav: ['Accueil', 'Enveloppes', 'Activité', 'Analyses'],
      available: 'Disponible',
      received: 'Reçu ce mois',
      spent: 'Dépensé ce mois',
      week: 'Dépenses de la semaine',
      days: ['L', 'M', 'M', 'J', 'V', 'S', 'D'],
      envelopes: 'Tes enveloppes',
      envelopeNames: ['Nourriture et eau', 'Loyer et factures', 'Transport', 'Épargne'],
      add: 'Ajouter une dépense',
    },
    sourcesTitle: 'Pour tous tes revenus, quelle que soit leur forme',
    sources: ['Salaire', 'Mobile Money', 'Espèces', 'Tontine', 'Commerce', 'Missions'],
    features: {
      badge: 'Fonctions',
      title: 'Tout pour tenir ton budget, rien de superflu.',
      body: 'Chaque écran a été pensé pour une seule chose : savoir, en un regard, ce que tu peux encore dépenser.',
      items: [
        {
          title: 'Saisie en trois gestes',
          body: 'Montant, enveloppe, valider. La dépense est enregistrée avant même que tu ranges ton portefeuille.',
        },
        {
          title: 'Des enveloppes qui parlent',
          body: 'Chaque enveloppe montre ce qui reste et te prévient quand le rythme de dépense s’emballe.',
        },
        {
          title: 'Échéances sans oubli',
          body: 'Loyer, électricité, internet : tes paiements fixes apparaissent à leur date. Un geste pour les marquer payés, jamais deux fois.',
        },
        {
          title: 'Un manque, une solution',
          body: 'Si une enveloppe ne suffit pas, Centime propose où prendre la différence, dans le respect des règles que tu as fixées.',
        },
      ],
      mock: {
        amount: 'Montant',
        envelope: 'Enveloppe',
        save: 'Enregistrer',
        available: 'Disponible ce mois',
        bills: 'À payer',
        rent: 'Loyer',
        power: 'Électricité',
        internet: 'Internet',
        inDays: 'Dans 3 jours',
        tomorrow: 'Échéance du jour',
        paid: 'Payé',
        shortfall: 'Il manque 5 000 FCFA dans Sorties.',
        takeFrom: 'Prendre dans Imprévus',
        takeUnallocated: 'Prendre dans le non affecté',
      },
    },
    why: {
      badge: 'Pourquoi Centime',
      title: 'Pensé pour la vraie vie, ici.',
      body: 'Une méthode éprouvée, adaptée aux revenus irréguliers, aux espèces et au Mobile Money.',
      items: [
        {
          title: 'En francs CFA',
          body: 'Des montants entiers, sans centimes, comme dans la vraie vie.',
        },
        {
          title: 'Revenus irréguliers',
          body: 'Salaire, missions, tontine : chaque rentrée se répartit au moment où elle arrive.',
        },
        {
          title: 'Épargne protégée',
          body: 'Une enveloppe protégée ne sert jamais à combler les autres. Ton épargne reste ton épargne.',
        },
        {
          title: 'Mode discret',
          body: 'Masque tous les montants d’un geste quand quelqu’un regarde ton écran.',
        },
        {
          title: 'Aucune banque connectée',
          body: 'Tu saisis ce que tu veux. Aucune donnée bancaire ne t’est demandée.',
        },
        {
          title: 'Téléphone et ordinateur',
          body: 'La même application, installée sur ton téléphone ou ouverte sur ton ordinateur.',
        },
      ],
    },
    how: {
      badge: 'Fonctionnement',
      title: 'Trois gestes pour reprendre la main.',
      body: 'Une méthode simple, que tu appliques dès ton premier revenu.',
      steps: [
        {
          title: 'Tu reçois',
          body: 'Ton salaire ou une rentrée ponctuelle arrive dans le non affecté. Rien n’est dépensable tant que tu ne l’as pas réparti.',
        },
        {
          title: 'Tu répartis',
          body: 'Centime propose une répartition selon tes objectifs. Tu ajustes, tu valides : chaque franc a sa place.',
        },
        {
          title: 'Tu dépenses sereinement',
          body: 'Montant, enveloppe, valider. Tu vois ce qu’il reste, et Centime te prévient avant que ça coince.',
        },
      ],
      mock: {
        received: 'Revenu reçu',
        salary: 'Salaire d’octobre',
        toAllocate: 'À répartir',
        allocated: 'Réparti',
        remaining: 'Il te reste',
        today: 'Aujourd’hui',
        market: 'Marché',
        taxi: 'Zémidjan',
      },
    },
    insights: {
      badge: 'Analyses',
      title: 'Ton mois en un coup d’œil.',
      body: 'Tu vois où part ton argent, jour après jour, et tu ajustes avant la fin du mois plutôt qu’après.',
      points: [
        'Dépenses par jour et par enveloppe',
        'Alertes quand le rythme s’emballe',
        'Journal complet, annulation possible',
        'Historique mois par mois',
      ],
      cta: 'Essayer maintenant',
      chart: 'Dépenses par jour',
      watch: 'À surveiller',
      watchBody: 'Au rythme actuel, Transport sera vide le 24.',
    },
    install: {
      badge: 'Application',
      title: 'Installe Centime sur ton téléphone.',
      body: 'Une icône sur ton écran d’accueil, une ouverture instantanée, et tu restes connecté. Aucun store, aucun téléchargement lourd.',
      points: ['Ouverture instantanée', 'Toujours connecté', 'Mises à jour automatiques'],
      cta: 'Installer l’application',
      open: 'Ouvrir l’application',
      installed: 'Centime est déjà installé sur cet appareil.',
      iosTitle: 'Installer sur iPhone',
      iosSteps: [
        'Ouvre mescentimes.cloud dans Safari.',
        'Touche le bouton Partager, en bas de l’écran.',
        'Choisis « Sur l’écran d’accueil », puis « Ajouter ».',
      ],
      manualTitle: 'Installer sur ce téléphone',
      manualSteps: [
        'Ouvre mescentimes.cloud dans Chrome.',
        'Touche le menu ⋮ en haut à droite.',
        'Choisis « Installer l’application » ou « Ajouter à l’écran d’accueil ».',
      ],
      understood: 'J’ai compris',
      imageAlt:
        'Centime installé sur l’écran d’accueil d’un iPhone, à côté des autres applications.',
    },
    pricing: {
      badge: 'Tarif',
      title: 'Gratuit. Vraiment.',
      body: 'Pas d’abonnement, pas de publicité, pas de revente de données. Centime est gratuit, et le restera pour l’essentiel.',
      plan: 'Centime',
      price: '0',
      unit: 'FCFA par mois',
      note: 'Toutes les fonctions, sans limite de temps.',
      cta: 'Créer mon compte',
      items: [
        'Enveloppes illimitées',
        'Échéances et paiements fixes',
        'Analyses du mois',
        'Mode discret',
        'Téléphone et ordinateur',
        'Suppression du compte à tout moment',
      ],
    },
    faq: {
      badge: 'Questions',
      title: 'Tes questions, nos réponses.',
      body: 'Une autre question ? Écris-nous depuis obafemiagnoun.cloud, on te répond vite.',
      items: [
        {
          question: 'Centime est-il gratuit ?',
          answer: 'Oui. Toutes les fonctions présentées sur cette page sont gratuites.',
        },
        {
          question: 'Dois-je connecter mon compte bancaire ?',
          answer:
            'Non. Tu enregistres toi-même tes revenus et tes dépenses. C’est rapide, et c’est justement ce qui te fait garder la main sur ton argent.',
        },
        {
          question: 'Mes données sont-elles en sécurité ?',
          answer:
            'Elles sont chiffrées pendant leur transfert, isolées compte par compte, et ne sont jamais vendues ni utilisées pour de la publicité.',
        },
        {
          question: 'J’avais un compte sur l’ancienne version. Que devient-il ?',
          answer:
            'Ton compte est conservé : connecte-toi avec la même adresse et le même mot de passe. Les anciennes données n’ont pas été reprises, tu repars sur une base propre.',
        },
        {
          question: 'Puis-je supprimer mon compte ?',
          answer:
            'Oui, depuis les réglages. Ton compte et toutes tes données sont effacés immédiatement et définitivement.',
        },
      ],
    },
    final: {
      title: 'Ton prochain salaire mérite un plan.',
      body: 'Crée ton compte en une minute et répartis ton argent dès aujourd’hui.',
      cta: 'Commencer maintenant',
    },
    footer: {
      tagline: 'Le budget par enveloppes, pensé pour la zone franc CFA.',
      product: 'Produit',
      account: 'Compte',
      legal: 'Légal',
      signUp: 'Créer un compte',
      legalLink: 'Mentions légales',
      termsLink: 'Conditions d’utilisation',
      privacy: 'Confidentialité',
      rights: (year: number) => `© ${String(year)} Centime. Fait à Abomey-Calavi.`,
    },
  },
  terms: {
    title: 'Conditions d’utilisation',
    updated: 'Mise à jour le 16 octobre 2026',
    sections: [
      {
        title: '1. Objet',
        body: 'Centime est une application gratuite d’aide à la gestion d’un budget personnel par enveloppes, éditée par Obafemi Agnoun. Ces conditions encadrent son utilisation. En créant un compte, tu les acceptes.',
      },
      {
        title: '2. Ton compte',
        body: 'Tu fournis une adresse e-mail valide et tu gardes ton mot de passe et ton code de verrouillage pour toi. Un compte correspond à une personne. Tu es responsable de ce qui est fait depuis ton compte ; préviens-nous sans tarder si tu penses qu’il a été utilisé par quelqu’un d’autre.',
      },
      {
        title: '3. Ce que fait Centime, et ce qu’il ne fait pas',
        body: 'Centime t’aide à suivre et répartir ton argent. Il n’exécute aucun paiement, virement ou opération bancaire ou Mobile Money : les montants sont ceux que tu saisis. Centime ne donne pas de conseil financier, juridique ou fiscal ; les décisions sur ton argent restent les tiennes.',
      },
      {
        title: '4. Gratuité et évolution',
        body: 'Centime est gratuit. Ses fonctions peuvent évoluer. Si une offre payante apparaît un jour, elle sera présentée clairement et ne te sera jamais imposée sans ton accord.',
      },
      {
        title: '5. Disponibilité',
        body: 'Nous faisons notre possible pour que Centime soit disponible et que tes données soient sauvegardées, sans pouvoir garantir l’absence totale d’interruption ou d’erreur. Centime ne peut être tenu responsable des pertes indirectes liées à son utilisation.',
      },
      {
        title: '6. Tes données',
        body: 'Leur traitement est décrit dans les mentions légales et la politique de confidentialité. Tu peux supprimer ton compte et toutes tes données à tout moment depuis les réglages.',
      },
      {
        title: '7. Communications',
        body: 'Les e-mails liés à ton compte et à sa sécurité sont toujours envoyés. Les rappels, les nouveautés et les conseils peuvent être désactivés dans les réglages ou depuis le lien présent en bas de chaque e-mail.',
      },
      {
        title: '8. Usage acceptable',
        body: 'Il est interdit de tenter d’accéder aux données d’autrui, de perturber le service, d’en faire un usage automatisé abusif ou d’y saisir des contenus illicites.',
      },
      {
        title: '9. Fin d’utilisation',
        body: 'Tu peux arrêter à tout moment en supprimant ton compte. Un compte qui enfreint ces conditions peut être suspendu, après t’avoir prévenu sauf urgence.',
      },
      {
        title: '10. Modifications',
        body: 'En cas de changement important de ces conditions, tu en seras informé par e-mail ou dans l’application au moins 15 jours avant son entrée en vigueur.',
      },
      {
        title: '11. Droit applicable et contact',
        body: 'Ces conditions sont soumises au droit béninois. Pour toute question ou difficulté, écris-nous depuis obafemiagnoun.cloud : nous chercherons d’abord une solution amiable.',
      },
    ],
    back: 'Retour',
  },
  legal: {
    title: 'Mentions légales et confidentialité',
    updated: 'Mise à jour le 15 octobre 2026',
    sections: [
      {
        title: 'Éditeur',
        body: 'Centime est édité par Obafemi Agnoun, développeur indépendant, à Abomey-Calavi (Bénin). Contact : obafemiagnoun.cloud.',
      },
      {
        title: 'Hébergement',
        body: 'Le site est hébergé par Netlify, Inc. (San Francisco, États-Unis). Les données sont stockées par Supabase, Inc. (San Francisco, États-Unis).',
      },
      {
        title: 'Données collectées',
        body: 'Ton adresse e-mail et ton prénom (facultatif) pour ton compte, ainsi que les enveloppes, revenus et dépenses que tu saisis. Aucune donnée bancaire n’est collectée.',
      },
      {
        title: 'Utilisation',
        body: 'Tes données servent uniquement à faire fonctionner Centime pour toi. Elles ne sont ni vendues, ni louées, ni utilisées pour de la publicité. L’administrateur voit des statistiques d’usage (nombre d’inscrits, jours d’activité), jamais tes montants ni tes libellés.',
      },
      {
        title: 'Tes droits',
        body: 'Tu peux demander l’accès, la correction ou la suppression de tes données à tout moment, depuis obafemiagnoun.cloud.',
      },
      {
        title: 'Cookies',
        body: 'Centime n’utilise aucun cookie publicitaire ni outil de mesure d’audience. Seules les données techniques nécessaires à ta session sont gardées dans ton navigateur.',
      },
    ],
    back: 'Retour à l’accueil',
  },
  ui: {
    back: 'Retour',
    close: 'Fermer',
    cancel: 'Annuler',
    choose: 'Choisir',
    validate: 'Valider',
    amountHidden: 'Montant masqué',
    hideAmounts: 'Masquer les montants',
    showAmounts: 'Afficher les montants',
    expandMenu: 'Déplier le menu',
    collapseMenu: 'Replier le menu',
    search: 'Rechercher',
    noResult: 'Aucun résultat.',
    today: 'Aujourd’hui',
    yesterday: 'Hier',
    previousMonth: 'Mois précédent',
    nextMonth: 'Mois suivant',
    showPassword: 'Afficher le mot de passe',
    hidePassword: 'Masquer le mot de passe',
    keypad: 'Pavé numérique',
    erase: 'Effacer le dernier chiffre',
    amountKeyboardHint: 'Tu peux aussi taper le montant au clavier.',
    pinProgress: (entered: number, total: number) =>
      `${String(entered)} chiffre${entered > 1 ? 's' : ''} saisi${entered > 1 ? 's' : ''} sur ${String(total)}`,
    notifications: 'Notifications',
    mainNavigation: 'Navigation principale',
    outOf: 'sur',
    reversed: 'annulée',
    envelopeProgress: (name: string) => `Disponible dans ${name}`,
    step: (current: number, total: number) => `Étape ${String(current)} sur ${String(total)}`,
    status: {
      normal: 'Normal',
      watch: 'Vigilance',
      critical: 'Critique',
      protected: 'Protégée',
      unfunded: 'À financer',
      overdue: 'En retard',
      due: 'Aujourd’hui',
      settled: 'Payée',
    },
    sync: {
      synced: 'Synchronisé',
      pending: 'En attente',
      conflict: 'Conflit à résoudre',
      offline: 'Hors ligne',
    },
    envelopeIcon,
  },
} as const
